import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Building, Home as HomeIcon, Users, ArrowRight, CheckCircle2, Download, Phone, Shield, Clock, FileCheck } from 'lucide-react';
import { stats, services, whyChooseUs } from '../mock';

const iconMap = {
  award: Award,
  building: Building,
  home: HomeIcon,
  users: Users
};

const trustBadges = [
  { label: '26 Years Experience', icon: Award },
  { label: '10M+ Sq Ft Completed', icon: Building },
  { label: '5000+ Clients', icon: Users },
  { label: 'Government Approved', icon: Shield }
];

const technologies = [
  {
    name: 'Crystalline Waterproofing Systems',
    problem: 'Deep concrete penetration for permanent protection',
    benefit: 'Self-healing properties, long-term protection',
    lifespan: 'Up to 10 years',
    suitable: 'Government, Residential, Industrial',
    standards: 'IS 2645, CPWD compliant'
  },
  {
    name: 'Polyurethane (PU) Liquid Membranes',
    problem: 'Seamless waterproofing for complex surfaces',
    benefit: 'High elasticity, UV resistant, walkable',
    lifespan: 'Up to 8 years',
    suitable: 'Terraces, Balconies, Wet Areas',
    standards: 'IS 15065 compliant'
  },
  {
    name: 'APP/SBS Modified Bitumen Membranes',
    problem: 'Heavy-duty waterproofing for large areas',
    benefit: 'High tensile strength, weather resistant',
    lifespan: 'Up to 10 years',
    suitable: 'Government Buildings, Infrastructure',
    standards: 'IS 15351 compliant'
  },
  {
    name: 'Injection Grouting Systems',
    problem: 'Active leakage repair without demolition',
    benefit: 'Quick solution, minimal disruption',
    lifespan: 'Up to 7 years',
    suitable: 'Basements, Tunnels, Structures',
    standards: 'CPWD approved materials'
  },
  {
    name: 'Food-Grade Tank Coatings',
    problem: 'Safe drinking water storage',
    benefit: 'Non-toxic, bacteria resistant',
    lifespan: 'Up to 8 years',
    suitable: 'Water Tanks, Sumps',
    standards: 'IS 12288, FSSAI approved'
  },
  {
    name: 'HDPE/PVC Membrane Systems',
    problem: 'Chemical and root resistant barriers',
    benefit: 'Zero water absorption, flexible',
    lifespan: 'Up to 10 years',
    suitable: 'Industrial, Infrastructure',
    standards: 'IS 7903 compliant'
  }
];

const processSteps = [
  {
    step: 1,
    title: 'Site Inspection & Technical Assessment',
    description: 'Detailed site survey, moisture mapping, structural evaluation',
    timeline: 'Day 1-2'
  },
  {
    step: 2,
    title: 'Root Cause Analysis',
    description: 'Identify source of water ingress, substrate condition check',
    timeline: 'Day 2-3'
  },
  {
    step: 3,
    title: 'System Recommendation & BOQ',
    description: 'Material specification, detailed quotation, timeline planning',
    timeline: 'Day 3-4'
  },
  {
    step: 4,
    title: 'Professional Execution',
    description: 'Surface preparation, system application, quality checks',
    timeline: 'As per scope'
  },
  {
    step: 5,
    title: 'Post-Completion Inspection & Warranty',
    description: 'Water test, documentation, warranty certificate issuance',
    timeline: 'Final day'
  }
];

export const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section - Enhanced */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white py-20 lg:py-32 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1591645321243-3adc1e75cfdc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHx3YXRlcnByb29maW5nJTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc2ODY0MjEwMnww&ixlib=rb-4.1.0&q=85"
            alt="Professional Waterproofing Work"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-50/95 to-white/95"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-full text-sm font-semibold text-blue-800 mb-6">
              <Shield className="w-4 h-4 mr-2" />
              Government-Grade Solutions | CPWD Compliant
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Government-Grade Waterproofing Solutions with 26 Years of Proven Execution
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-4 leading-relaxed">
              10M+ sq ft completed across government infrastructure and residential projects. Compliance-ready systems with long-term warranty-backed protection.
            </p>
            <p className="text-base text-slate-500 mb-8">
              Serving 60% Government Infrastructure | 40% Residential | Jabalpur & Central India
            </p>
            
            {/* Primary & Secondary CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-slate-800 text-white rounded-lg text-base font-semibold hover:bg-slate-900 transition-colors shadow-lg"
              >
                <Phone className="mr-2 w-5 h-5" />
                Book Technical Site Inspection
              </Link>
              <a
                href="/roofsnwalls-brochure.pdf"
                download="RoofsnWalls_Company_Profile.pdf"
                className="inline-flex items-center justify-center px-8 py-4 bg-white border-2 border-slate-300 text-slate-700 rounded-lg text-base font-semibold hover:border-slate-400 hover:bg-slate-50 transition-colors"
              >
                <Download className="mr-2 w-5 h-5" />
                Download Company Profile
              </a>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {trustBadges.map((badge, index) => {
                const IconComponent = badge.icon;
                return (
                  <div key={index} className="flex flex-col items-center p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
                    <IconComponent className="w-6 h-6 text-slate-700 mb-2" />
                    <span className="text-xs font-medium text-slate-700 text-center">{badge.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-slate-800 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = iconMap[stat.icon];
              return (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-slate-300" />
                    </div>
                  </div>
                  <div className="text-3xl lg:text-4xl font-bold text-white mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-300">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technology & Systems Section - NEW */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Modern Waterproofing Technologies We Use
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Industry-leading systems compliant with Indian Standards (IS codes) and CPWD specifications
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-slate-50 rounded-lg p-6 border border-slate-200 hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">{tech.name}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-slate-700">Application:</span>
                    <p className="text-slate-600">{tech.problem}</p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Advantage:</span>
                    <p className="text-slate-600">{tech.benefit}</p>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                    <div>
                      <p className="text-xs text-slate-500">Lifespan</p>
                      <p className="font-semibold text-slate-900">{tech.lifespan}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Standards</p>
                      <p className="font-semibold text-slate-900 text-xs">{tech.standards}</p>
                    </div>
                  </div>
                  <div className="pt-2">
                    <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded">{tech.suitable}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process Section - NEW */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Our Proven 5-Step Process
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Systematic execution ensuring quality at every stage
            </p>
          </div>
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-slate-300 -translate-y-1/2"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 relative">
              {processSteps.map((process) => (
                <div key={process.step} className="relative">
                  <div className="bg-white rounded-lg p-6 border-2 border-slate-200 hover:border-slate-400 transition-colors relative z-10">
                    <div className="w-12 h-12 bg-slate-800 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4 mx-auto">
                      {process.step}
                    </div>
                    <h3 className="text-base font-semibold text-slate-900 mb-2 text-center">{process.title}</h3>
                    <p className="text-sm text-slate-600 mb-3 text-center">{process.description}</p>
                    <div className="flex items-center justify-center space-x-1 text-xs text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span>{process.timeline}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Comprehensive Waterproofing Solutions
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From residential homes to government infrastructure, we deliver proven waterproofing systems
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-lg overflow-hidden hover:shadow-lg transition-shadow border border-slate-200"
              >
                {service.imageUrl && (
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={service.imageUrl}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-sm text-slate-600 mb-4">{service.problem}</p>
                  <div className="flex items-start space-x-2 mb-2">
                    <CheckCircle2 className="w-4 h-4 text-slate-700 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-slate-700">{service.benefit}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/services"
              className="inline-flex items-center px-6 py-3 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-900 transition-colors"
            >
              View Detailed Services
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Why roofsnwalls
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Experience, scale, and proven results across residential and government projects
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-800 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Get Technical Consultation Within 24 Hours
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Professional site inspection and customized waterproofing solution from our experienced team
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-slate-800 rounded-lg text-base font-semibold hover:bg-slate-100 transition-colors"
          >
            Request Technical Assessment
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};